import React from 'react';
import { Typography, Box, CircularProgress, Alert, Grid } from '@mui/material';
import { useApiGet } from '../../services/tanstackApiService';
import { Patientv2 } from '../../types';
import { useTranslation } from 'react-i18next';
import { toTitleCase } from '../../utils/commonUtils';
import { formatDateDMY } from '../../utils/dateUtils';

interface PatientDetailsForReportProps {
  patientId: number;
  /** When true, mask patient identity (name, DOB, mobile) for donor privacy */
  isDonor?: boolean;
}

// Compact row field for report
const FieldRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', py: 0.3, '@media print': { py: 0.15 } }}>
    <Typography 
      component="span"
      sx={{ 
        color: '#374151', 
        fontSize: 12, 
        fontWeight: 600,
        minWidth: 85,
        '@media print': { fontSize: 10, minWidth: 70, color: '#333' }
      }}
    >
      {label}
    </Typography>
    <Typography 
      component="span"
      sx={{ 
        color: '#374151', 
        fontSize: 12, 
        fontWeight: 600,
        '@media print': { fontSize: 10, color: '#333' }
      }}
    >
      : {value ?? '-'}
    </Typography>
  </Box>
);

/**
 * Hook to fetch patient details using TanStack Query
 */
function usePatientDetails(patientId: number) {
  return useApiGet<Patientv2>(
    ['patient-details', patientId],
    {
      path: `patients/${patientId}`,
    },
    {
      enabled: !!patientId && patientId > 0,
    }
  );
}

const PatientDetailsForReport: React.FC<PatientDetailsForReportProps> = ({ patientId, isDonor = false }) => {
  const { t: tGeneral } = useTranslation('general');
  
  const { data: patientData, isLoading, error } = usePatientDetails(patientId);

  if (isLoading) {
    return (
      <Box sx={{ py: 1, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error || !patientData) {
    return (
      <Box sx={{ py: 1 }}>
        <Alert severity="error" sx={{ py: 0.5 }}>Failed to load patient details</Alert>
      </Box>
    );
  }

  const patient = patientData;
  const partner = patient.partner;

  // Calculate age from DOB
  const calculateAge = (dob?: string) => {
    if (!dob) return '';
    const birthDate = new Date(dob);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    return `${age} Yrs`;
  };

  return (
    <Box 
      sx={{ 
        mt: 1.5,
        mb: 1,
        py: 0.75,
        px: 1.5,
        backgroundColor: '#f8fafc',
        borderRadius: 0.5,
        border: '1px solid #e5e7eb',
        '@media print': {
          mt: 0,
          mb: 0.5,
          py: 0.25,
          px: 0,
          backgroundColor: 'transparent',
          border: 'none',
          borderBottom: '1px solid #ddd',
          borderRadius: 0
        }
      }}
    >
      <Grid container spacing={0}>
        {/* Patient Section - Left Half */}
        <Grid item xs={partner ? 6 : 12} sx={{ pr: partner ? 2 : 0 }}>
          <Grid container spacing={0}>
            <Grid item xs={6}>
              {isDonor ? (
                <FieldRow label={tGeneral('patientType', { defaultValue: 'Patient Type' })} value="Donor" />
              ) : (
                <FieldRow label={tGeneral('patientName')} value={`${patient.first_name} ${patient.last_name}`} />
              )}
              <FieldRow label={tGeneral('patientId')} value={patient.medical_record_no || '-'} />
              {!isDonor && <FieldRow label={tGeneral('DOB')} value={formatDateDMY(patient.date_of_birth)} />}
            </Grid>
            <Grid item xs={6}>
              {!isDonor && <FieldRow label={`${tGeneral('gender')} / Age`} value={`${toTitleCase(patient.gender) || '-'} / ${calculateAge(patient.date_of_birth)}`} />}
              {!isDonor && <FieldRow label={tGeneral('mobileNo')} value={patient.mobile_number || '-'} />}
            </Grid>
          </Grid>
        </Grid>

        {/* Partner Section - Right Half */}
        {partner && (
          <Grid item xs={6} sx={{ pl: 2, borderLeft: '1px solid #e5e7eb', '@media print': { borderColor: '#ccc', pl: 1.5 } }}>
            <Grid container spacing={0}>
              <Grid item xs={6}>
                <FieldRow label={tGeneral('partner')} value={`${partner.first_name} ${partner.last_name}`} />
                <FieldRow label={tGeneral('partnerId')} value={partner.medical_record_no || '-'} />
                <FieldRow label={tGeneral('partnerDOB')} value={formatDateDMY(partner.date_of_birth)} />
              </Grid>
              <Grid item xs={6}>
                <FieldRow label={`${tGeneral('gender')} / Age`} value={`${toTitleCase(partner.gender) || '-'} / ${calculateAge(partner.date_of_birth)}`} />
                <FieldRow label={tGeneral('mobileNo')} value={partner.mobile_number || '-'} />
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default PatientDetailsForReport;
